import { ReactElement, useState } from 'react';
import { useSelector } from 'react-redux';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  faCalendar,
  faChartLine,
  faCheck,
  faCircleInfo,
  faClock,
  faComment,
  faExchangeAlt,
  faFileAlt,
  faGavel,
  faHeading,
  faLink,
  faPencil,
  faUserPlus,
  faUsers,
} from '@fortawesome/free-solid-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { BillSummary } from '@enview/interface/types/bills/BillSummary';
import {
  BillDiffUpdateSummary,
  BillMentionUpdate,
  BillUpdateType,
} from '@enview/interface/types/triage/BillUpdateIndicator';
import ConfirmActionPopover from '../Popovers/ConfirmActionPopover';
import useMarkBillsAsRead from '../BillTriage/useMarkBillsAsRead';
import { BillTriageAPI } from '../../../api';
import { getOrgForTeamMode } from '../../../dux';
import './UnreadUpdatesIndicator.scss';

const UPDATE_TYPE_META: Record<
  BillUpdateType,
  { icon: IconDefinition; label: string }
> = {
  [BillUpdateType.NewRelatedBill]: { icon: faLink, label: 'New Related Bill' },
  [BillUpdateType.NewBillAction]: { icon: faChartLine, label: 'New Bill Action' },
  [BillUpdateType.NewAmendment]: { icon: faPencil, label: 'New Amendment' },
  [BillUpdateType.NewBillStatus]: { icon: faExchangeAlt, label: 'Bill Status Changed' },
  [BillUpdateType.NewBillTitle]: { icon: faHeading, label: 'Bill Title Changed' },
  [BillUpdateType.NewSponsor]: { icon: faUserPlus, label: 'New Sponsor' },
  [BillUpdateType.NewVersion]: { icon: faFileAlt, label: 'New Version' },
  [BillUpdateType.NewVoteOutcome]: { icon: faGavel, label: 'New Vote Outcome' },
  [BillUpdateType.NewCommitteeMention]: {
    icon: faUsers,
    label: 'New Committee Mention',
  },
  [BillUpdateType.RelatedHearings]: { icon: faCalendar, label: 'Related Hearing' },
  [BillUpdateType.HearingSoon]: { icon: faClock, label: 'Hearing Soon' },
  [BillUpdateType.NewMention]: { icon: faComment, label: 'New Mention' },
};

const formatOccurredAt = (iso: string): string =>
  new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

interface UnreadUpdateRow {
  key: string;
  icon: IconDefinition;
  label: string;
  description: string | null;
  occurredAt: string;
}

// Changes with no listed update type still keep a bill unread, so they get a generic row
// rather than being left out of what Mark as read clears.
const OTHER_CHANGES_META = { icon: faCircleInfo, label: 'Other Changes' };

const getWorkspaceNames = (diffs: BillDiffUpdateSummary[]): string[] =>
  Array.from(new Set(diffs.flatMap((diff) => diff.workspaceNames))).sort((a, b) =>
    a.localeCompare(b),
  );

// Names each change's workspaces only when the popover spans several of them.
const toDiffRows = (
  diffs: BillDiffUpdateSummary[],
  showWorkspaceNames: boolean,
): UnreadUpdateRow[] =>
  diffs.flatMap((diff) => {
    const description = showWorkspaceNames ? diff.workspaceNames.join(', ') : null;
    const metas = diff.updateTypes.length
      ? diff.updateTypes.map((updateType) => ({
          key: updateType,
          ...UPDATE_TYPE_META[updateType],
        }))
      : [{ key: 'other', ...OTHER_CHANGES_META }];
    return metas.map((meta) => ({
      key: `${diff.billDifferenceId}-${meta.key}`,
      icon: meta.icon,
      label: meta.label,
      description,
      occurredAt: diff.occurredAt,
    }));
  });

const toMentionRows = (mentions: BillMentionUpdate[]): UnreadUpdateRow[] =>
  mentions.map((mention) => ({
    key: `mention-${mention.billAnnotationId}`,
    icon: mention.annotationType === 'assignment' ? faUserPlus : faComment,
    label: mention.annotationType === 'assignment' ? 'Assigned to You' : 'New Comment',
    description:
      mention.annotationType === 'comment' && mention.commentPreview
        ? `${mention.authorName}: ${mention.commentPreview}`
        : mention.authorName,
    occurredAt: mention.occurredAt,
  }));

interface UnreadUpdatesIndicatorProps {
  bill: BillSummary;
}

// Bill Title cell for the Updated Bills / Assigned to Me sections only: shows a dot next
// to the title when the bill has unread updates, and opens a popover on click listing
// what changed since the viewer (or their workspaces, for changes) last marked it seen,
// across every workspace Mark as read clears it for.
const UnreadUpdatesIndicator = (props: UnreadUpdatesIndicatorProps): ReactElement => {
  const { bill } = props;

  const organization = useSelector(getOrgForTeamMode);
  const orgId = organization?.id;

  const [show, setShow] = useState(false);

  const { data, isFetching } = BillTriageAPI.endpoints.getBillUpdateIndicators.useQuery(
    show && orgId ? { orgId, billId: bill.id } : skipToken,
  );
  const markBillsAsRead = useMarkBillsAsRead();

  if (!bill.hasUnreadUpdates) {
    return <span className="bill-text-truncated">{bill.name}</span>;
  }

  const handleToggle = (): void => setShow((prev) => !prev);
  const handleClose = (): void => setShow(false);

  const diffs = data?.diffs ?? [];
  const workspaceNames = getWorkspaceNames(diffs);
  const rows = [
    ...toDiffRows(diffs, workspaceNames.length > 1),
    ...toMentionRows(data?.mentions ?? []),
  ].sort((a, b) => new Date(b.occurredAt).getTime() - new Date(a.occurredAt).getTime());

  const handleMarkAsRead = (): void => {
    handleClose();
    if (!orgId) return;
    void markBillsAsRead({ orgId, billIds: [bill.id] });
  };

  return (
    <ConfirmActionPopover
      align="start"
      className="unread-updates-popover"
      closePopup={handleClose}
      content={
        show ? (
          <div className="unread-updates-content">
            <ul className="unread-updates-list">
              {isFetching && <li className="unread-updates-empty">Loading…</li>}
              {!isFetching && !rows.length && (
                <li className="unread-updates-empty">
                  Nothing new since your last view.
                </li>
              )}
              {rows.map((row) => (
                <li className="unread-updates-item" key={row.key}>
                  <FontAwesomeIcon className="unread-updates-icon" icon={row.icon} />
                  <div className="unread-updates-item-body">
                    <div className="unread-updates-item-header">
                      <span className="unread-updates-item-label">{row.label}</span>
                      <span className="unread-updates-item-time">
                        {formatOccurredAt(row.occurredAt)}
                      </span>
                    </div>
                    {row.description && (
                      <p className="unread-updates-item-description">
                        {row.description}
                      </p>
                    )}
                  </div>
                </li>
              ))}
            </ul>
            {/* Changes are marked read per workspace, so this clears them for teammates too;
                mentions are only ever marked read for the viewer. */}
            {workspaceNames.length > 0 && (
              <p className="unread-updates-scope-note">
                Marking as read clears these changes for everyone in{' '}
                {workspaceNames.join(', ')}.
              </p>
            )}
            <button
              className="unread-updates-mark-read"
              onClick={handleMarkAsRead}
              type="button"
            >
              <FontAwesomeIcon icon={faCheck} />
              Mark as read
            </button>
          </div>
        ) : null
      }
      hideCloseIcon
      label="What changed since you last viewed"
      onClickOutside={handleClose}
      positions={['bottom', 'top', 'right', 'left']}
      show={show}
      showArrowContainer={false}
      target={
        <span
          className="bill-title-unread-trigger bill-text-truncated"
          title="See what changed"
        >
          <span className="bill-title-unread-dot" />
          {bill.name}
        </span>
      }
      toggleCallback={handleToggle}
    />
  );
};

export default UnreadUpdatesIndicator;
